import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, inject } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { renderRichText } from '@storyblok/js';
import { PricingService } from 'src/app/services/pricing.service';
import { PromotionService } from 'src/app/services/promotion.service';
import { StoryblokService } from 'src/app/services/storyblok.service';
import { SvgComponent } from 'src/app/shared/components/svg/svg.component';
import { ButtonDirective } from 'src/app/shared/directives/button.directive';
import { ResizeDirective } from 'src/app/shared/directives/resize.directive';
import { TooltipDirective } from 'src/app/shared/directives/tooltip.directive';
import { ProductPrice } from 'src/app/types/product.type';
import { Promotion } from 'src/app/types/promotion.type';
import Stripe from 'stripe';
import { PricingBoxesComponent } from '../../../shared/components/pricing-boxes/pricing-boxes.component';
import { CustomCurrencyPipe } from '../../../shared/pipes/custom-currency.pipe';
import { StoryblokDirective } from '../../storyblok.directive';
import { BlockStyle, BlockWrapperComponent } from '../block-wrapper/block-wrapper.component';

@Component({
  selector: 'app-pricing-box-list',
  standalone: true,
  templateUrl: './pricing-box-list.component.html',
  styleUrls: ['./pricing-box-list.component.scss'],
  imports: [
    CommonModule,
    BlockWrapperComponent,
    PricingBoxesComponent,
    ButtonDirective,
    SvgComponent,
    TooltipDirective,
    CustomCurrencyPipe,
    RouterLink,
    ResizeDirective,
    StoryblokDirective,
  ],
  providers: [CustomCurrencyPipe],
})
export class PricingBoxListComponent implements OnChanges {
  private pricingService = inject(PricingService);
  private route = inject(ActivatedRoute);
  private customCurrencyPipe = inject(CustomCurrencyPipe);
  private promotionService = inject(PromotionService);
  private storyblokService = inject(StoryblokService);
  @Input() _uid: string; // just here to prevent errors
  @Input() component: string; // just here to prevent errors
  @Input() _editable: string;
  @Input() replacer?: (key: string) => string;
  @Input() style: BlockStyle[];

  @Input() boxes: string[];
  isAtleastOnePromo: boolean;
  allowTrial: boolean;

  products: {
    id: string;
    name: string;
    color: string;
    points: {
      icon: string;
      text: string;
      tooltipContent: string;
      _editable?: string;
    }[];
    description: string;
    prices: ProductPrice[];
    isFree: boolean;
    priceDetails: ReturnType<PricingService['getPriceDetails']>;
    _editable?: string;
  }[] = [];

  productPromos: { [key: string]: Promotion } = {};

  ngOnChanges() {
    const backendPrices: Stripe.Price[] = this.storyblokService.getGlobalData('backendPrices');
    const relationships = this.storyblokService.getGlobalData('relationships');
    this.allowTrial = !!this.pricingService.getTrialLengthDays();

    this.products = this.boxes
      .map(id => {
        const data = relationships.find(box => box.uuid === id);
        if (!data) return;

        const productLink = relationships.find(
          rel => rel.content.component === 'PricingTechnicalLink' && rel.uuid === data.content.product
        );

        const prices = backendPrices
          .filter(price => {
            return (
              productLink.content.lookupKeyMonthly === price.lookup_key ||
              productLink.content.lookupKeyYearly === price.lookup_key
            );
          })
          .map(price => this.pricingService.convertPrice(price));

        const product = {
          name: data.name,
          points: data.content.points.map(point => {
            return {
              ...point,
              tooltipContent: renderRichText(point.tooltipContent),
            };
          }),
          description: data.content.description,
          prices,
          color: productLink.content.color,
          isFree: data.name.toLowerCase().includes('free'),
          id: prices[0].product,
          priceDetails: undefined,
        };

        product.priceDetails = this.getPriceDetails(product);

        return product;
      })
      .filter(Boolean);
    this.setupPromos();
  }

  getPriceDetails(product) {
    return this.pricingService.getPriceDetails(product.prices, this.productPromos[product.id], 'yearly');
  }

  getTooltipContent(product) {
    const priceInfo = product.priceDetails;

    const prefix = `<h3>Pricing</h3>`;
    const suffix = `<br><br>Do you work with freelancers and clients? Invite external guests for free!`;

    const toPrice = this.customCurrencyPipe.transform;

    return `${prefix}
      US${toPrice(priceInfo.yearlyAmount / 12)} per member, per month billed yearly or US${toPrice(
      priceInfo.monthlyAmount
    )} month-to-month.
      ${suffix}`;
  }

  getUrl(product) {
    if (product.isFree) return '/auth/signup/free';
    let link = `/auth/signup/${product.name.toLowerCase()}/yearly`;
    if (this.productPromos[product.id]) link += `/${this.productPromos[product.id].code}`;
    return link;
  }

  private setupPromos() {
    const promotion = this.route.snapshot.data.promotion;
    this.productPromos = {};
    if (promotion) {
      this.products.forEach(product => {
        if (this.promotionService.appliesTo(promotion, product, 'yearly')) {
          this.productPromos[product.id] = promotion;
        }
      });
    }
    this.isAtleastOnePromo = Object.values(this.productPromos).length > 0;
  }
}
